import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

interface VideoSectionProps {
  videoId?: string;
  videoType?: 'youtube' | 'loom';
  title?: string;
  description?: string;
}

const VideoSection: React.FC<VideoSectionProps> = ({ 
  videoId = "dQw4w9WgXcQ",
  videoType = "youtube",
  title = "See Zylu in Action",
  description = "Watch how salon owners transform their business with Zylu's powerful automation features"
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(videoRef, { 
    once: false, 
    margin: "-100px",
    amount: 0.3 
  });

  const getEmbedUrl = () => {
    if (videoType === 'loom') {
      return `https://www.loom.com/embed/${videoId}?autoplay=1&muted=0&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`;
    }
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&muted=0&rel=0&modestbranding=1&origin=${window.location.origin}`;
  };

  return (
    <section className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {description}
          </p>
        </motion.div>

        <motion.div
          ref={videoRef}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative max-w-4xl mx-auto"
        >
          <div className="bg-gray-800 rounded-2xl overflow-hidden shadow-2xl">
            <div className="aspect-video">
              {isInView ? (
                <iframe
                  key={`${videoType}-${videoId}`}
                  src={getEmbedUrl()}
                  title="Zylu Demo Video"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-16 h-16 mx-auto mb-4 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-lg font-medium">Video will load when in focus</p>
                    <p className="text-sm text-gray-300 mt-2">Scroll down to view • Audio enabled</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoSection;